import {
    Box, Card, CardContent, FormControlLabel, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
} from "@mui/material";
import React, { useState } from "react";
import CardTitle from "./CardTitle";
import MiniField from "./MiniField";
import { useDispatch, useSelector } from "react-redux";
import { updateDataFilterSetting } from "../../reducer/CalcSettingsSlice";
import { DataFilterTypes, capitalizeFirstLetter, roundToX } from "../../common/common";
import { numberSx, tokens } from "../../theme";

/**
 * Replaces NoiseReductionStepCard's single fixed 25um tolerance with a per-axis, per-segment
 * one measured from each scan's own data (tolerance = Multiplier x residual std). Runs with
 * PreFilter: false, so - like FocusRemoveWorst - it gets one trace entry each for "machine" and
 * "motor" rather than NoiseReductionStep's single "all" entry, hence the machine/motor table
 * instead of NoiseReductionStepCard's flat stat list.
 */
export default function NoiseReductionScatterCard(props) {
    const calcSettings = useSelector(state => state.calcSettings);
    const laserData = useSelector(state => state.laserData);
    const nrSettings = calcSettings.changes.dataFilters.find(df => df.type === DataFilterTypes.NoiseReductionScatter);
    const [iterationError, setIterationError] = useState(false);
    const [multiplierError, setMultiplierError] = useState(false);
    const [keepBestError, setKeepBestError] = useState(false);
    const dispatch = useDispatch();

    const handleEnableChange = (evt) => {
        dispatch(updateDataFilterSetting({
            type: DataFilterTypes.NoiseReductionScatter,
            enabledState: true,
            value: evt.target.checked,
            error: false,
        }));
    }

    const handleIterationsChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) === 0);
            setIterationError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.NoiseReductionScatter,
                property: "Iterations",
                value: evt.target.value,
                error: error
            }));
        }
    }

    const handleMultiplierChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*\.?[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) === 0);
            setMultiplierError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.NoiseReductionScatter,
                property: "Multiplier",
                value: evt.target.value,
                error: error
            }));
        }
    }

    const handleKeepBestChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) === 0);
            setKeepBestError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.NoiseReductionScatter,
                property: "KeepBestPercent",
                value: String(evt.target.value / 100),
                error: error
            }));
        }
    }

    // Column widths/labels matched to FocusRemoveWorst.js's own table exactly - single-word
    // headers only, since anything longer wrapped onto two lines at rail width there.
    const headerCellSx = {
        fontSize: "10.5px", fontWeight: 600, color: tokens.muted,
        textTransform: "uppercase", letterSpacing: "0.04em", border: "none", padding: "3px 4px",
        whiteSpace: "nowrap",
    };
    const rowHeaderCellSx = {
        fontSize: "12px", fontWeight: 600, color: tokens.textSoft, border: "none", padding: "3px 4px",
        whiteSpace: "nowrap",
    };
    const numCellSx = { ...numberSx, fontSize: "12px", border: "none", padding: "3px 4px" };

    const renderTraceTable = () => {
        const rows = ["machine", "motor"]
            .map((dataType) => ({
                dataType,
                filter: laserData.filterTrace.filters.find(f => f.type === "NoiseReductionScatter" && f.data === dataType),
            }))
            .filter((row) => row.filter);

        if (rows.length === 0) {
            return <></>;
        }

        return (
            <TableContainer>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell align="right" sx={headerCellSx}>Initial</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Kept</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Passes</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Std Y</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Std Z</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ dataType, filter }) => (
                            <TableRow key={dataType}>
                                <TableCell component="th" scope="row" sx={rowHeaderCellSx}>
                                    {capitalizeFirstLetter(dataType)}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.initialPointCount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.keptPointCount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.iterations}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{roundToX(filter.trace.stdY, 1)}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{roundToX(filter.trace.stdZ, 1)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    const renderAttributes = () => {
        if (!nrSettings) {
            return "no settings";
        }
        return (
            <Box>
                <Box display="flex" justifyContent="left" sx={{ paddingLeft: "5px" }}>
                    <FormControlLabel
                        label="Enabled"
                        labelPlacement="end"
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '0.875rem'
                            }
                        }}
                        control={
                            <Switch
                                checked={nrSettings.enabled}
                                onChange={handleEnableChange}
                                size="small"
                            />
                        }
                    >
                    </FormControlLabel>
                </Box>
                {nrSettings.enabled && (
                    <>
                        <Box sx={{ display: "flex", gap: "8px", mb: "10px" }}>
                            <MiniField
                                label="Iterations"
                                value={nrSettings.attributes.Iterations}
                                onChange={handleIterationsChange}
                                maxLength={3}
                                error={iterationError}
                            />
                            <MiniField
                                label="Multiplier (k)"
                                value={nrSettings.attributes.Multiplier}
                                onChange={handleMultiplierChange}
                                maxLength={4}
                                error={multiplierError}
                            />
                            <MiniField
                                label="Keep best %"
                                value={(isNaN(nrSettings.attributes.KeepBestPercent)) ? 50 : roundToX(nrSettings.attributes.KeepBestPercent * 100, 0)}
                                onChange={handleKeepBestChange}
                                maxLength={2}
                                error={keepBestError}
                            />
                        </Box>
                        {renderTraceTable()}
                    </>
                )}
            </Box>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <CardTitle title="Noise Reduction by Scatter" />
                {renderAttributes()}
            </CardContent>
        </Card>
    )
}
